<template>
  <v-dialog v-model="showDialog" max-width="1400px" persistent>
    <v-card>
      <!-- ✅ HEADER -->
      <v-card-title class="primary white--text">
        <v-icon left color="white">mdi-calendar-week</v-icon>
        Horarios Semanales: {{ workerName }}
        <v-spacer></v-spacer>
        <v-chip color="white" text-color="primary" small>
          {{ formatWeekRange(currentWeekStart, currentWeekEnd) }}
        </v-chip>
        <v-btn icon color="white" @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- ✅ NAVEGACIÓN SEMANAL -->
      <v-toolbar flat color="grey lighten-4">
        <v-btn icon @click="previousWeek" :disabled="loading">
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        <v-spacer></v-spacer>
        <h3>Semana: {{ formatWeekRange(currentWeekStart, currentWeekEnd) }}</h3>
        <v-spacer></v-spacer>
        <v-btn icon @click="nextWeek" :disabled="loading">
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-0">
        <!-- ✅ LOADING -->
        <v-progress-linear
          v-if="loading"
          indeterminate
          color="primary"
        ></v-progress-linear>

        <!-- ✅ TABLA SEMANAL CON EDITOR INLINE -->
        <v-simple-table class="weekly-schedule-table" v-if="!loading">
          <template v-slot:default>
            <thead>
              <tr style="background: #37474f; color: white">
                <th class="day-column text-center">Día</th>
                <th class="schedule-column text-center">Horario laboral</th>
                <th class="break-column text-center">Refrigerio</th>
                <!-- <th class="min-break-column text-center">Min Ref.</th> -->
                <th class="actions-column text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="day in weekDays"
                :key="day.ScheduleDate"
                :class="getDayRowClass(day)"
              >
                <!-- ✅ DÍA -->
                <td class="day-cell text-center">
                  <div class="day-info">
                    <strong>{{ day.DayName }}</strong>
                    <br />
                    <small>{{ formatShortDate(day.ScheduleDate) }}</small>
                    <br />
                    <v-chip :color="getStatusColor(day.DayStatus)" dark x-small>
                      {{ getStatusLabel(day.DayStatus) }}
                    </v-chip>
                  </div>
                </td>

                <!-- ✅ HORARIO LABORAL (HLAB) -->
                <td class="schedule-cell text-center">
                  <template v-if="day.DayStatus === 'PROGRAMADO'">
                    <div class="schedule-container">
                      <!-- Badge de horas -->
                      <div class="hours-badge purple white--text mb-2">
                        {{ formatHours(day.HoursRequired) }}
                      </div>
                      <!-- Rango de horas -->
                      <div class="time-range orange lighten-4 pa-2 ma-1">
                        <div class="time-display">
                          {{ formatTime(day.HLAB_SddHourBegin) }}
                        </div>
                        <div class="time-separator">-</div>
                        <div class="time-display">
                          {{ formatTime(day.HLAB_SddHourEnd) }}
                        </div>
                      </div>
                    </div>
                  </template>
                  <div v-else class="rest-cell">---</div>
                </td>

                <!-- ✅ REFRIGERIO (HREF) -->
                <td class="break-cell text-center">
                  <template
                    v-if="
                      day.DayStatus === 'PROGRAMADO' && day.HREF_SddHourBegin
                    "
                  >
                    <div class="break-container">
                      <div class="hours-badge green white--text mb-2">
                        {{ formatHours(day.HoursBreak) }}
                      </div>
                      <div class="time-range purple lighten-4 pa-2 ma-1">
                        <div class="time-display">
                          {{ formatTime(day.HREF_SddHourBegin) }}
                        </div>
                        <div class="time-separator">-</div>
                        <div class="time-display">
                          {{ formatTime(day.HREF_SddHourEnd) }}
                        </div>
                      </div>
                    </div>
                  </template>
                  <template v-else-if="day.DayStatus === 'PROGRAMADO'">
                    <div class="no-break">Sin refrigerio</div>
                  </template>
                  <div v-else class="rest-cell">---</div>
                </td>

                <!-- ✅ ACCIONES - USAR InlineScheduleEditor -->
                <td class="actions-cell text-center">
                  <template
                    v-if="day.DayStatus === 'PROGRAMADO' && day.CacheID"
                  >
                    <!-- ✅ USAR EL COMPONENTE EXISTENTE -->
                    <inline-schedule-editor
                      :item="adaptDayForEditor(day)"
                      @save="onUpdateDaySchedule"
                    />

                    <!-- ✅ BOTÓN ELIMINAR -->
                    <!-- <v-btn
                      icon
                      x-small
                      color="red"
                      @click="deleteSchedule(day)"
                      class="ml-1"
                      :disabled="!day.CacheID"
                    >
                      <v-icon x-small>mdi-delete</v-icon>
                    </v-btn> -->
                  </template>

                  <template v-else-if="day.DayStatus === 'FALTA'">
                    <!-- ✅ BOTÓN AGREGAR HORARIO -->
                    <v-btn
                      icon
                      x-small
                      color="green"
                      @click="addSchedule(day)"
                      title="Agregar horario"
                    >
                      <v-icon x-small>mdi-plus</v-icon>
                    </v-btn>
                  </template>

                  <div v-else class="rest-cell">---</div>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>

        <!-- ✅ RESUMEN DE HORAS -->
        <v-card-text v-if="hasWorkingDays">
          <v-row>
            <v-col cols="4">
              <v-card outlined>
                <v-card-text class="text-center">
                  <h3 class="primary--text">{{ formatHours(totalLaboral) }}</h3>
                  <div>Horas Laborales</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="4">
              <v-card outlined>
                <v-card-text class="text-center">
                  <h3 class="orange--text">
                    {{ formatHours(totalRefrigerio) }}
                  </h3>
                  <div>Horas Refrigerio</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="4">
              <v-card outlined>
                <v-card-text class="text-center">
                  <h3 class="green--text">
                    {{ formatHours(totalLaboral - totalRefrigerio) }}
                  </h3>
                  <div>Horas Netas</div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import _sHmnHoursWorkedService from "@/services/HumanResource/HmnHoursWorkedService";
import InlineScheduleEditor from "./InlineScheduleEditor.vue";

export default {
  name: "WeeklyScheduleModal",
  components: {
    InlineScheduleEditor,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    wkrId: {
      type: Number,
      required: true,
    },
    workerName: {
      type: String,
      default: "",
    },
    initDate: {
      type: String,
      default: null, // ✅ CAMBIAR A NULL
      validator: (value) => value === null || typeof value === "string",
    },
    endDate: {
      type: String,
      default: null, // ✅ CAMBIAR A NULL
      validator: (value) => value === null || typeof value === "string",
    },
  },
  data() {
    return {
      loading: false,
      weekDays: [],
      currentWeekStart: null,
      currentWeekEnd: null,
    };
  },
  computed: {
    showDialog: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    validInitDate() {
      return this.initDate;
    },

    validEndDate() {
      return this.endDate;
    },
    totalLaboral() {
      return this.weekDays
        .filter((day) => day.DayStatus === "PROGRAMADO")
        .reduce((sum, day) => sum + (day.HoursRequired || 0), 0);
    },

    totalRefrigerio() {
      return this.weekDays
        .filter((day) => day.DayStatus === "PROGRAMADO")
        .reduce((sum, day) => sum + (day.HoursBreak || 0), 0);
    },

    hasWorkingDays() {
      return this.weekDays.some((day) => day.DayStatus === "PROGRAMADO");
    },
  },
  watch: {
    value(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          // ✅ CARGAR SOLO SI TENEMOS FECHAS VÁLIDAS
          if (this.initDate && this.endDate) {
            this.currentWeekStart = this.initDate;
            this.currentWeekEnd = this.endDate;
            this.loadWeeklySchedule();
          } else {
            console.warn("⚠️ No hay fechas válidas para cargar");
          }
        });
      }
    },
    initDate: {
      handler(newVal, oldVal) {
        // console.log("👀 initDate prop changed:", { old: oldVal, new: newVal });
        if (newVal && newVal !== oldVal) {
          this.currentWeekStart = newVal;
          // Solo cargar si también tenemos endDate y el dialog está abierto
          if (this.endDate && this.value) {
            this.currentWeekEnd = this.endDate;
            this.loadWeeklySchedule();
          }
        }
      },
      immediate: false,
    },

    endDate: {
      handler(newVal, oldVal) {
        // console.log("👀 endDate prop changed:", { old: oldVal, new: newVal });
        if (newVal && newVal !== oldVal) {
          this.currentWeekEnd = newVal;
          // Solo cargar si también tenemos initDate y el dialog está abierto
          if (this.initDate && this.value) {
            this.currentWeekStart = this.initDate;
            this.loadWeeklySchedule();
          }
        }
      },
      immediate: false,
    },
  },

  methods: {
    async onUpdateDaySchedule({ item, newValues, oldValues }) {

      try {
        // ✅ MOSTRAR LOADING MIENTRAS SE ACTUALIZA
        this.loading = true;

        // ✅ RECARGAR TODOS LOS DATOS DE LA SEMANA
        await this.loadWeeklySchedule();

        this.$fun.alert(
          `Horario de ${item.DayName} actualizado correctamente`,
          "success"
        );

        // console.log("✅ Datos recargados después de actualización");
      } catch (error) {
        console.error(
          "❌ Error al recargar datos después de actualización:",
          error
        );
        this.$fun.alert("Error al recargar los datos actualizados", "error");
      } finally {
        this.loading = false;
      }
    },
    async recalculateHours(day) {
      // Esto es opcional - podrías hacer una llamada al servidor para recalcular
      // O calcular localmente las horas
      if (day.HLAB_SddHourBegin && day.HLAB_SddHourEnd) {
        const start = new Date(day.HLAB_SddHourBegin);
        const end = new Date(day.HLAB_SddHourEnd);
        const diffHours = (end - start) / (1000 * 60 * 60);
        day.HoursRequired = diffHours;
      }

      if (day.HREF_SddHourBegin && day.HREF_SddHourEnd) {
        const start = new Date(day.HREF_SddHourBegin);
        const end = new Date(day.HREF_SddHourEnd);
        const diffHours = (end - start) / (1000 * 60 * 60);
        day.HoursBreak = diffHours;
      }
    },
    async deleteSchedule(day) {
      const confirm = await this.$fun.confirm(
        `¿Está seguro de eliminar el horario de ${day.DayName}?`,
        "Confirmar eliminación"
      );

      if (!confirm) return;

      try {
        // ✅ LLAMAR ENDPOINT DE ELIMINACIÓN
        await _sHmnHoursWorkedService.deleteScheduleDay(
          day.CacheID,
          this.$fun.getUserID()
        );

        // ✅ ACTUALIZAR LOCALMENTE
        const dayIndex = this.weekDays.findIndex(
          (d) => d.CacheID === day.CacheID
        );
        if (dayIndex !== -1) {
          this.weekDays[dayIndex] = {
            ...this.weekDays[dayIndex],
            DayStatus: "FALTA",
            HLAB_SddHourBegin: null,
            HLAB_SddHourEnd: null,
            HREF_SddHourBegin: null,
            HREF_SddHourEnd: null,
            HoursRequired: 0,
            HoursBreak: 0,
            CacheID: null,
          };
        }

        this.$fun.alert(`Horario de ${day.DayName} eliminado`, "success");
      } catch (error) {
        console.error("Error al eliminar horario:", error);
        this.$fun.alert("Error al eliminar horario: " + error.message, "error");
      }
    },
    async loadWeeklySchedule() {
      const startDate = this.currentWeekStart;
      const endDate = this.currentWeekEnd;

      if (!startDate || !endDate) {
        console.warn("⚠️ Fechas no válidas para cargar horarios");
        return;
      }

      this.loading = true;
      try {
        const response = await _sHmnHoursWorkedService.getWeeklySchedule(
          this.wkrId,
          startDate,
          endDate,
          this.$fun.getUserID()
        );

        // console.log("✅ Respuesta del servidor:", response);

        let weeklyData = [];

        if (response && response.data) {
          weeklyData = Array.isArray(response.data) ? response.data : [];
        } else if (Array.isArray(response)) {
          weeklyData = response;
        }

        // console.log("📅 Datos extraídos:", weeklyData);

        if (Array.isArray(weeklyData)) {
          this.weekDays = weeklyData;
          this.weekDays.sort((a, b) => a.RangeDay - b.RangeDay);

        } else {
          console.error("❌ Los datos no son un array:", weeklyData);
          this.weekDays = [];
          this.$fun.alert(
            "Error: Los datos recibidos no tienen el formato esperado",
            "error"
          );
        }
      } catch (error) {
        console.error("❌ Error completo:", error);
        this.weekDays = [];
        this.$fun.alert(
          "Error al cargar los horarios semanales: " +
            (error.response?.data?.message || error.message),
          "error"
        );
      } finally {
        this.loading = false;
      }
    },
    async forceRefresh() {
      console.log("🔄 Forzando actualización de datos...");
      await this.loadWeeklySchedule();
    },
    adaptDayForEditor(day) {
      return {
        CacheID: day.CacheID,
        WkrName: this.workerName,
        WkrID: this.wkrId,
        ScheduleDate: day.ScheduleDate,
        DayName: day.DayName,
        HLAB_SddHourBegin: day.HLAB_SddHourBegin,
        HLAB_SddHourEnd: day.HLAB_SddHourEnd,
        HREF_SddHourBegin: day.HREF_SddHourBegin,
        HREF_SddHourEnd: day.HREF_SddHourEnd,
        HoursRequired: day.HoursRequired,
        HoursBreak: day.HoursBreak,
      };
    },

    previousWeek() {
      if (!this.currentWeekStart) return;

      // ✅ CREAR NUEVOS OBJETOS MOMENT SIN MUTAR EL ORIGINAL
      const currentStart = this.$moment(this.currentWeekStart);
      const newStart = currentStart
        .clone()
        .subtract(7, "days")
        .format(this.$const.FormatDateDB);
      const newEnd = currentStart
        .clone()
        .subtract(7, "days")
        .add(6, "days")
        .format(this.$const.FormatDateDB);

      this.currentWeekStart = newStart;
      this.currentWeekEnd = newEnd;

      this.$emit("week-changed", { initDate: newStart, endDate: newEnd });
      this.loadWeeklySchedule();
    },

    // ✅ ARREGLAR nextWeek - crear nuevos objetos moment
    nextWeek() {
      if (!this.currentWeekStart) return;

      // ✅ CREAR NUEVOS OBJETOS MOMENT SIN MUTAR EL ORIGINAL
      const currentStart = this.$moment(this.currentWeekStart);
      const newStart = currentStart
        .clone()
        .add(7, "days")
        .format(this.$const.FormatDateDB);
      const newEnd = currentStart
        .clone()
        .add(7, "days")
        .add(6, "days")
        .format(this.$const.FormatDateDB);

      this.currentWeekStart = newStart;
      this.currentWeekEnd = newEnd;

      this.$emit("week-changed", { initDate: newStart, endDate: newEnd });
      this.loadWeeklySchedule();
    },

    closeDialog() {
      this.showDialog = false;
      this.$emit("close");
    },

    // ✅ MÉTODOS DE FORMATEO
    formatWeekRange(start, end) {
      if (!start || !end) return "";
      const startFormatted = this.$moment(start).format("DD/MM");
      const endFormatted = this.$moment(end).format("DD/MM/YYYY");
      return `${startFormatted} - ${endFormatted}`;
    },

    formatShortDate(date) {
      if (!date) return "";
      return this.$moment(date).format("DD/MM");
    },

    formatTime(datetime) {
      if (!datetime) return "--:--";
      return this.$moment(datetime).format("HH:mm");
    },

    formatHours(hours) {
      if (!hours || hours === 0) return "00:00";
      const totalMinutes = Math.round(hours * 60);
      const h = Math.floor(totalMinutes / 60);
      const m = totalMinutes % 60;
      return `${h.toString().padStart(2, "0")}:${m
        .toString()
        .padStart(2, "0")}`;
    },

    // ✅ MÉTODOS DE ESTADO
    getDayRowClass(day) {
      switch (day.DayStatus) {
        case "SIN_CONTRATO":
          return "row-no-contract";
        case "CONTRATO_TERMINADO":
          return "row-contract-ended";
        case "FALTA":
          return "row-missing";
        case "PROGRAMADO":
          return "row-scheduled";
        default:
          return "";
      }
    },

    getStatusColor(status) {
      switch (status) {
        case "SIN_CONTRATO":
          return "grey";
        case "CONTRATO_TERMINADO":
          return "red";
        case "FALTA":
          return "orange";
        case "PROGRAMADO":
          return "green";
        default:
          return "grey";
      }
    },

    getStatusLabel(status) {
      switch (status) {
        case "SIN_CONTRATO":
          return "S/C";
        case "CONTRATO_TERMINADO":
          return "FIN";
        case "FALTA":
          return "DESCANSO";
        case "PROGRAMADO":
          return "OK";
        default:
          return "?";
      }
    },

    getRestLabel(status) {
      switch (status) {
        case "SIN_CONTRATO":
          return "Sin contrato";
        case "CONTRATO_TERMINADO":
          return "Contrato terminado";
        case "FALTA":
          return "DESCANSO";
        default:
          return "---";
      }
    },

    // ✅ MÉTODOS DE EDICIÓN (PLACEHOLDER)
    editSchedule(day, type) {
      this.$fun.alert(`Editar ${type} para ${day.DayName}`, "info");
      // TODO: Implementar modal de edición
    },

    deleteSchedule(day, type) {
      this.$fun.alert(`Eliminar ${type} para ${day.DayName}`, "warning");
      // TODO: Implementar eliminación
    },

    addSchedule(day, type) {
      this.$fun.alert(`Agregar ${type} para ${day.DayName}`, "info");
      // TODO: Implementar adición
    },

    bulkEdit(type) {
      this.$fun.alert(`Edición masiva de ${type}`, "info");
      // TODO: Implementar edición masiva
    },
    generateWeekFromDate(baseDate, weekOffset = 0) {
      const selectedDate = this.$moment(baseDate);

      // Calcular lunes de esa semana
      const dayOfWeek = selectedDate.day(); // 0=Domingo, 1=Lunes, ..., 6=Sábado
      const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

      // Obtener lunes base y aplicar offset de semanas
      const monday = selectedDate
        .clone()
        .subtract(mondayOffset, "days")
        .add(weekOffset * 7, "days");

      const sunday = monday.clone().add(6, "days");

      return {
        start: monday.format(this.$const.FormatDateDB),
        end: sunday.format(this.$const.FormatDateDB),
      };
    },
  },

  created() {
    // ✅ INICIALIZAR weekDays COMO ARRAY VACÍO
    this.weekDays = [];

    // ✅ NO INICIALIZAR FECHAS AQUÍ - esperar a que lleguen por props
    // Las fechas se inicializarán en los watchers cuando lleguen las props
  },
};
</script>

<style scoped>
/* ✅ ESTILOS PARA LA TABLA SEMANAL */
.weekly-schedule-table {
  border-collapse: collapse;
}

.day-column {
  width: 120px;
  min-width: 120px;
}

.schedule-column {
  width: 200px;
  min-width: 200px;
}

.break-column {
  width: 150px;
  min-width: 150px;
}

.min-break-column {
  width: 80px;
  min-width: 80px;
}

.actions-column {
  width: 100px;
  min-width: 100px;
}

.day-info {
  padding: 8px;
}

.schedule-container,
.break-container {
  padding: 4px;
}

.time-range {
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 32px;
}

.time-display {
  font-weight: bold;
  font-size: 12px;
}

.time-separator {
  margin: 0 4px;
  font-weight: bold;
}

.hours-badge {
  border-radius: 12px;
  padding: 2px 8px;
  font-size: 10px;
  font-weight: bold;
}

.rest-cell {
  color: #666;
  font-style: italic;
}

.no-break {
  color: #999;
  font-size: 12px;
}
</style>
